/**
 * SplitMoveStopsDialog — full-screen modal that lets an admin split a tour
 * or move selected stops into another tour.
 *
 * Contract:
 *   props.open              — controls visibility
 *   props.onClose()         — called when the user cancels / X'es out
 *   props.onSuccess({ destination_tour_id, created_new_tour })
 *                             — called after a successful move so the parent
 *                               can toast + optionally redirect
 *   props.sourceTour        — { tour_id, title }
 *   props.selectedStops     — array of { stop_id, stop_number, title } in current-order
 *
 * The dialog does its own tour-list fetch. Never touches the source tour's
 * editor state; the parent handles refresh after onSuccess.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Search, ArrowRight, PlusCircle, ChevronRight, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyNewTour = {
  title: '',
  city: '',
  description: '',
  duration: '1 hour',
  base_price: 10,
  group_addon_price: 5,
  is_free: false,
  cover_image_url: '',
  published: false,
};

const SplitMoveStopsDialog = ({ open, onClose, onSuccess, sourceTour, selectedStops }) => {
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [tours, setTours] = useState([]);
  const [search, setSearch] = useState('');
  const [destTourId, setDestTourId] = useState('');
  const [destStops, setDestStops] = useState([]);
  const [insertMode, setInsertMode] = useState('end'); // 'beginning'|'end'|'before'|'after'
  const [refStopId, setRefStopId] = useState('');
  const [newTour, setNewTour] = useState(emptyNewTour);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  // Load the tour catalog once when the dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tours`, { headers });
        setTours((data || []).filter((t) => t.tour_id !== sourceTour?.tour_id));
      } catch (_) {
        toast.error('Could not load your tours');
      }
    })();
    // Reset local state so the dialog is fresh each time
    setMode('existing');
    setDestTourId('');
    setDestStops([]);
    setInsertMode('end');
    setRefStopId('');
    setNewTour(emptyNewTour);
    setConfirming(false);
    setSubmitting(false);
    setSearch('');
  }, [open, sourceTour?.tour_id]);

  // Fetch the destination tour's stops so the user can pick an insertion point
  useEffect(() => {
    if (!destTourId) { setDestStops([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/tours/${destTourId}/stops`, { headers });
        setDestStops(data || []);
      } catch (_) {
        setDestStops([]);
      }
    })();
  }, [destTourId]);

  const filteredTours = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tours;
    return tours.filter((t) => `${t.title || ''} ${t.city || ''}`.toLowerCase().includes(q));
  }, [tours, search]);

  const chosenTour = tours.find((t) => t.tour_id === destTourId) || null;
  const chosenRefStop = destStops.find((s) => s.stop_id === refStopId) || null;

  const canProceed = () => {
    if (!selectedStops || selectedStops.length === 0) return false;
    if (mode === 'existing') {
      if (!destTourId) return false;
      if ((insertMode === 'before' || insertMode === 'after') && !refStopId) return false;
      return true;
    }
    // new tour path
    return newTour.title.trim().length > 0 && newTour.city.trim().length > 0;
  };

  const summaryText = () => {
    const n = selectedStops.length;
    const noun = n === 1 ? 'stop' : 'stops';
    const destLabel = mode === 'new' ? `new tour "${newTour.title || '(untitled)'}"` : `"${chosenTour?.title || ''}"`;
    let where;
    if (mode === 'new') {
      where = 'at the beginning (new tour starts empty)';
    } else if (insertMode === 'beginning') {
      where = 'at the beginning';
    } else if (insertMode === 'end') {
      where = 'at the end';
    } else if (insertMode === 'before' && chosenRefStop) {
      where = `before stop ${chosenRefStop.stop_number} — "${chosenRefStop.title}"`;
    } else if (insertMode === 'after' && chosenRefStop) {
      where = `after stop ${chosenRefStop.stop_number} — "${chosenRefStop.title}"`;
    } else {
      where = '';
    }
    return `Move ${n} ${noun} from "${sourceTour?.title}" to ${destLabel}, ${where}.`;
  };

  const submitMove = async () => {
    setSubmitting(true);
    const body = {
      stop_ids: selectedStops.map((s) => s.stop_id),
      insert_position: {
        mode: mode === 'new' ? 'end' : insertMode,
        reference_stop_id: (mode === 'existing' && (insertMode === 'before' || insertMode === 'after')) ? refStopId : null,
      },
    };
    if (mode === 'existing') {
      body.destination_tour_id = destTourId;
    } else {
      body.new_tour = { ...newTour, base_price: Number(newTour.base_price) || 0 };
    }
    try {
      const { data } = await axios.post(`${API}/admin/stops/move`, body, { headers });
      toast.success(`Moved ${data.moved_count} stop${data.moved_count === 1 ? '' : 's'}`);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Move failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" data-testid="split-move-dialog">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-black text-slate-900">Split / Move Stops</h2>
            <p className="text-xs text-slate-500">
              {selectedStops.length} stop{selectedStops.length === 1 ? '' : 's'} selected from &quot;{sourceTour?.title}&quot;
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="split-move-close"
            className="p-2 text-slate-400 hover:text-slate-700 rounded-full"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!confirming ? (
          <div className="p-5 space-y-5">
            {/* Mode tabs */}
            <div className="grid grid-cols-2 rounded-xl overflow-hidden border border-slate-200">
              <button
                type="button"
                onClick={() => setMode('existing')}
                data-testid="mode-existing"
                className={`px-4 py-3 text-sm font-bold transition ${mode === 'existing' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <ArrowRight className="w-4 h-4 inline mr-2 -mt-0.5" />
                Move to existing tour
              </button>
              <button
                type="button"
                onClick={() => setMode('new')}
                data-testid="mode-new"
                className={`px-4 py-3 text-sm font-bold transition ${mode === 'new' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <PlusCircle className="w-4 h-4 inline mr-2 -mt-0.5" />
                Create a new tour
              </button>
            </div>

            {mode === 'existing' && (
              <>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Destination tour
                  </label>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search your tours…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="tour-search"
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {filteredTours.length === 0 ? (
                      <p className="text-sm text-slate-500 p-4 text-center">No matching tours.</p>
                    ) : filteredTours.map((t) => (
                      <button
                        key={t.tour_id}
                        onClick={() => { setDestTourId(t.tour_id); setInsertMode('end'); setRefStopId(''); }}
                        data-testid={`dest-tour-${t.tour_id}`}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 transition ${destTourId === t.tour_id ? 'bg-teal-50' : ''}`}
                      >
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate">{t.title}</p>
                          <p className="text-xs text-slate-500 truncate">{t.city}</p>
                        </div>
                        {destTourId === t.tour_id && <CheckCircle2 className="w-4 h-4 text-teal-600 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>

                {destTourId && (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      Insertion point
                    </label>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {[
                        { key: 'beginning', label: 'Beginning' },
                        { key: 'end', label: 'End' },
                        { key: 'before', label: 'Before a stop' },
                        { key: 'after', label: 'After a stop' },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => setInsertMode(opt.key)}
                          data-testid={`insert-${opt.key}`}
                          className={`px-3 py-2 rounded-lg text-sm font-bold border transition ${
                            insertMode === opt.key
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {(insertMode === 'before' || insertMode === 'after') && (
                      <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                        {destStops.length === 0 ? (
                          <p className="text-sm text-slate-500 p-4 text-center">
                            Destination has no stops yet. Choose Beginning or End.
                          </p>
                        ) : destStops.map((s) => (
                          <button
                            key={s.stop_id}
                            onClick={() => setRefStopId(s.stop_id)}
                            data-testid={`ref-stop-${s.stop_id}`}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${refStopId === s.stop_id ? 'bg-teal-50' : ''}`}
                          >
                            <span className="font-mono text-xs text-slate-500 w-8">#{s.stop_number}</span>
                            <span className="flex-1 truncate font-bold text-slate-800">{s.title}</span>
                            {refStopId === s.stop_id && <CheckCircle2 className="w-4 h-4 text-teal-600" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {mode === 'new' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  We&apos;ll create a new tour and move the selected stops into it. You can polish the details afterwards.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Title *</label>
                    <input
                      type="text"
                      value={newTour.title}
                      onChange={(e) => setNewTour({ ...newTour, title: e.target.value })}
                      data-testid="new-tour-title"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                      placeholder="Neve Tzedek Walk"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">City *</label>
                    <input
                      type="text"
                      value={newTour.city}
                      onChange={(e) => setNewTour({ ...newTour, city: e.target.value })}
                      data-testid="new-tour-city"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                      placeholder="Tel Aviv"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Description</label>
                  <textarea
                    value={newTour.description}
                    onChange={(e) => setNewTour({ ...newTour, description: e.target.value })}
                    data-testid="new-tour-description"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                    placeholder="A shorter walk through…"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Duration</label>
                    <input
                      type="text"
                      value={newTour.duration}
                      onChange={(e) => setNewTour({ ...newTour, duration: e.target.value })}
                      data-testid="new-tour-duration"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                      placeholder="45 min"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Price (USD)</label>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={newTour.base_price}
                      onChange={(e) => setNewTour({ ...newTour, base_price: e.target.value })}
                      data-testid="new-tour-price"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 pb-2">
                      <input
                        type="checkbox"
                        checked={newTour.is_free}
                        onChange={(e) => setNewTour({ ...newTour, is_free: e.target.checked })}
                        data-testid="new-tour-free"
                        className="w-4 h-4 accent-slate-900"
                      />
                      Free tour
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Cover image URL</label>
                  <input
                    type="text"
                    value={newTour.cover_image_url}
                    onChange={(e) => setNewTour({ ...newTour, cover_image_url: e.target.value })}
                    data-testid="new-tour-cover"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400"
                    placeholder="https://…"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={onClose}
                data-testid="split-move-cancel"
                className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                disabled={!canProceed()}
                onClick={() => setConfirming(true)}
                data-testid="split-move-next"
                className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white font-bold rounded-full px-5 py-2 text-sm"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          // Confirmation panel
          <div className="p-5 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-bold text-amber-900 mb-2">Confirm move</p>
              <p className="text-sm text-slate-800" data-testid="split-move-summary">{summaryText()}</p>
            </div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Every stop&apos;s content — GPS coordinates, audio, images, description, timestamps —
              stays exactly the same. Stop numbers are automatically renumbered on both tours after
              the move. This action is reversible by moving the stops back.
            </div>
            <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
              <button
                onClick={() => setConfirming(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={submitMove}
                disabled={submitting}
                data-testid="split-move-confirm"
                className="inline-flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-bold rounded-full px-5 py-2 text-sm"
              >
                {submitting ? 'Moving…' : 'Confirm move'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitMoveStopsDialog;
